// src/utils/salesOrder.js

import { formatToCompanyTimezone, formatToUtc, getDateTimeFromSelectedDate, getYearFromCompanyDate } from "../lib/functions";
import dayjs from 'dayjs';
import Big from "big.js";

const toNumber = (v, d = 0) => {
  if (v === null || v === undefined || v === "" || isNaN(v)) return d;
  const n = Number(v);
  return isFinite(n) ? n : d;
};

const toBig = (v, d = 0) => {
 try {
  return new Big(v ?? d);
 } catch {
  return new Big(d);
 }
};

// Discount for a single item row: reads whichever mode is active
// (percent, the default/undefined-safe mode, or a flat amount), clamped
// so a flat amount can never exceed the item's own line total.
const getItemDiscountAmount = (item, line) => {
  if (item?.discountMode === "amount") {
    const raw = toBig(item?.discountAmt);
    return raw.gt(line) ? line : raw.lt(0) ? new Big(0) : raw;
  }
  return line.times(toBig(item?.discount)).div(100);
};

export const createSalesOrderPayload = (state, authInfo) => {
 
  const {
    customerInfo,
    itemCart,
    paymentTerm,
    payServiceCart = [],
    footerValues,
    voucherNumber,
    tagFieldId,
    sale_o_id,
    selectedDiscountAcc,
    selectedTaxAcc,
    selectedTransportAcc,
    conversion_rate,
    conversion_currency,
    entryDate,
    poReference,
    dueDays,
    taxMode,
  } = state;

  // convert selected/entry Date to utc date + time
  let finalEntryDate = entryDate;
  if (!dayjs(entryDate).isSame(dayjs(), "day")) {
    finalEntryDate = getDateTimeFromSelectedDate(entryDate); // Merge date + current time in TZ

  } else {
    finalEntryDate = entryDate; // Use now in TZ
  }

  let dateUTC = formatToUtc(finalEntryDate);

  let companydate = formatToCompanyTimezone(dateUTC);
  const vchYear = getYearFromCompanyDate(companydate);

  // 🔹 map items with bonus/discount support
  const items = itemCart.map((r) => {
    const isBonus = Boolean(r.bonus);

    const qty = toBig(r.quantity);
    const rate = isBonus ? new Big(0) : toBig(r.rate);
    let line = qty.times(rate);
    const discountAmt = isBonus ? new Big(0) : getItemDiscountAmount(r, line);
    const afterDiscount = line.minus(discountAmt);

    // Back-computed so the printed voucher (which reads this field directly
    // in "on_item" calc mode) is correct even when the row is in amount mode.
    const discountPct = isBonus
      ? new Big(0)
      : r.discountMode === "amount"
        ? (line.eq(0) ? new Big(0) : discountAmt.div(line).times(100))
        : toBig(r.discount);

    const taxPer = isBonus ? new Big(0) : toBig(r.tax_per);
    const taxAmt = afterDiscount.times(taxPer).div(100);

    return {
      // core
      item_id: r.item_id,
      serials: r.serials || [],

      // 🔹 Backend expected names
      per_unit_id: r.per_unit_id || r.unit_id || 0,
      warehouse_id: r.warehouse_id || 0,
      item_description: r.item_description || "",

      item_qty: qty,
      item_rate: rate,
      item_total: r.bonus
        ? 0
        : Number((taxMode === "on_item" ? afterDiscount.plus(taxAmt) : line).toFixed(2)),

      item_discount_per: r.bonus ? 0 : discountPct,
      item_discount: r.bonus ? 0 : Number(discountAmt.toFixed(2)),
      discount_mode: r.discountMode === "amount" ? "amount" : "percent",

      item_tax_per: r.bonus ? 0 : taxPer,
      item_tax: r.bonus ? 0 : Number(taxAmt.toFixed(2)),

      // duplicate mappings backend expects
      sale_qty: qty,
      sale_rate: rate,
      retail_qty: 0,

      // accounts
      discount_acc_id:  toNumber(selectedDiscountAcc?.acc_id, 0),
      tax_acc_id: toNumber(selectedTaxAcc?.acc_id, 0),

      branch_id: r.branch_id || 2, // fallback from getItems API
    };
  });


  // 🔹 build payload
  const salesOrderData = {
    sale_o_id: sale_o_id || 0,
    itemCart: items,
    payServiceCart,
    vchdate: vchYear,

    customer: {
      acc_id: customerInfo?.acc_id || "",
      acc_name:
        (customerInfo?.acc_name || customerInfo?.display_text || "").trim(),
      institution_name: customerInfo?.institution_name || "",
      contact_no: customerInfo?.contact_no || "",
      country_code: customerInfo?.country_code || "",
      address: customerInfo?.address || "",
    },

    masterData: {
      sale_order_no: voucherNumber || "",
      is_serial: items.some((i) => i.is_serial === "yes") ? "yes" : "no",
      is_cal_type: taxMode === "on_total" ? "on_total" : "individual", // safe default
      created_date: formatToUtc(finalEntryDate || new Date().toISOString()),
      customer_po_ref: poReference || "",
      payment_terms: state.paymentTermsText || "",
      due_date: state.paymentTerm || 0, // ✅ null if not set

      narration: footerValues?.description || "",

      sub_total: Number(new Big(footerValues?.subtotal || 0).toFixed(2)),
      total_discount: Number(new Big(footerValues?.total_discount || 0).toFixed(2)),
      total_discount_per: toNumber(footerValues?.discountPct, 0),
      total_discount_mode: footerValues?.discountMode === "amount" ? "amount" : "percent",
      total_tax_per: taxMode === "on_total"
        ? toNumber(authInfo?.item_tax, 0)
        : 0,
      total_tax: Number(new Big(footerValues?.totalTax || 0).toFixed(2)),
      total_transport_cost: toNumber(footerValues?.total_transport_cost, 0),
      total_amount: Number(new Big(footerValues?.grandTotal || 0).toFixed(2)),

      discount_acc_id: selectedDiscountAcc?.acc_id | 0,
      tax_acc_id: selectedTaxAcc?.acc_id || 0,
      // tag_field_id: tagFieldId,
      transport_acc_id: selectedTransportAcc?.acc_id || 0,

      other_service_cost: payServiceCart.reduce(
        (sum, p) => sum.plus(p.tran_amount || 0),
        new Big(0)
      ).toFixed(2),

      conversion_rate: Number(new Big(conversion_rate || 0).toFixed(6)),
      conversion_currency: conversion_currency || null,
    },
  };

  if (tagFieldId) {
    salesOrderData.masterData.tag_field_id = tagFieldId;
  }

  return salesOrderData;
};
